import {
  IsOptional,
  IsInt,
  IsPositive,
  Min,
  IsString,
  IsNumber,
  IsDateString,
  IsArray,
  ArrayMaxSize,
} from 'class-validator';
import { Transform } from 'class-transformer';
import { ApiPropertyOptional } from '@nestjs/swagger';

/**
 * DTO para filtrar la búsqueda de liquidaciones de agencia.
 * Permite filtrar por cliente, temporada, tipo de servicio, comisión mínima, paginación y ordenamiento.
 *
 * Uso: Se utiliza en el endpoint GET /api/v1/liquidaciones-agencia para filtrar resultados.
 */
export class LiquidacionAgenciaFilterDto {
  /**
   * ID del cliente a filtrar. Si no se especifica, depende del rol del usuario.
   * - Cliente: solo puede ver su propio ID.
   * - Superuser: puede seleccionar cualquier cliente.
   */
  @ApiPropertyOptional({ description: 'ID del cliente' })
  @IsOptional()
  @IsInt()
  clienteId?: number;

  /**
   * ID de la temporada a consultar.
   * Si no se especifica, se usan todas las temporadas activas.
   * Ejemplos: 7 (2024-2025), 6 (2023-2024)
   * NOTA: Si se especifican dateFrom/dateTo, estos tienen prioridad sobre temporada.
   */
  @ApiPropertyOptional({ description: 'ID de la temporada' })
  @IsOptional()
  @IsInt()
  temporada?: number;

  /**
   * Tipo de liquidación (punto A.1.1 de la solicitud de OPB, ago-2026).
   * Se filtra por ID y no por nombre: el pedido dice "ReLiquidación" y la base
   * guarda "Reliquidación", así que comparar el texto es frágil.
   * - 1 = Liquidación
   * - 2 = Reliquidación
   */
  @ApiPropertyOptional({
    description: 'Tipo de liquidación: 1 = Liquidación, 2 = Reliquidación',
    enum: [1, 2],
  })
  @IsOptional()
  @IsInt()
  tipoId?: number;

  /**
   * N° de liquidación. Acepta el número pelado (1613) o el formato que muestra
   * la interfaz con ceros a la izquierda (000001613).
   */
  @ApiPropertyOptional({
    description: 'Número de liquidación (con o sin ceros a la izquierda)',
    example: '000001613',
  })
  @IsOptional()
  @IsString()
  nroLiquidacion?: string;

  /**
   * N° de factura de venta. Sale de `tblShippingLiquidationCustomerInvoices`
   * (`InvoiceNumber`), que es grano FACTURA: una factura puede agrupar varias
   * liquidaciones, y ése es justamente el caso de uso pedido — buscar la factura
   * 1169 debe traer sus 4 liquidaciones.
   */
  @ApiPropertyOptional({
    description: 'Número de factura de venta',
    example: '1169',
  })
  @IsOptional()
  @IsString()
  nroFactura?: string;

  /**
   * N° de nota de cobro. Misma tabla que la factura, campo `SaleNoteNumber`.
   */
  @ApiPropertyOptional({
    description: 'Número de nota de cobro',
    example: '1172',
  })
  @IsOptional()
  @IsString()
  nroNotaCobro?: string;

  /**
   * Estado de la liquidación (punto A.2 de la solicitud de OPB, ago-2026).
   * Valores reales de la base: 1 = Activo, 2 = Liquidado, 4 = Finalizado.
   * NO existe un estado "Anulada" — verificado sobre `vwShippingLiquidations`.
   *
   * El DEFECTO "Finalizado" se aplica en la PANTALLA, no acá: el pedido es de
   * presentación del módulo, y ponerlo en el backend cambiaría la respuesta para
   * todos los consumidores del endpoint sin que lo hayan pedido.
   */
  @ApiPropertyOptional({
    description:
      'Estado de la liquidación: 1 = Activo, 2 = Liquidado, 4 = Finalizado',
    enum: [1, 2, 4],
  })
  @IsOptional()
  @IsInt()
  estadoId?: number;

  /**
   * Fecha de inicio del rango a consultar (YYYY-MM-DD).
   * Si se especifica, tiene PRIORIDAD sobre el filtro de temporada.
   * Debe usarse junto con dateTo.
   */
  @ApiPropertyOptional({
    description:
      'Fecha de inicio del rango (YYYY-MM-DD). Prioridad sobre temporada',
    example: '2024-11-01',
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  /**
   * Fecha de fin del rango a consultar (YYYY-MM-DD).
   * Si se especifica, tiene PRIORIDAD sobre el filtro de temporada.
   * Debe usarse junto con dateFrom.
   */
  @ApiPropertyOptional({
    description:
      'Fecha de fin del rango (YYYY-MM-DD). Prioridad sobre temporada',
    example: '2024-11-30',
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;

  /**
   * Tipo de servicio de agencia a filtrar.
   * Ejemplos: 'agenciamiento-import', 'agenciamiento-export', 'documentacion', 'coordinacion'
   */
  @ApiPropertyOptional({
    description: 'Tipo de servicio de agencia',
    example: 'agenciamiento-import',
    enum: [
      'agenciamiento-import',
      'agenciamiento-export',
      'documentacion',
      'coordinacion',
    ],
  })
  @IsOptional()
  @IsString()
  tipoServicio?: string;

  /**
   * Filtro por comisión mínima. Solo muestra liquidaciones con comisión >= valor especificado.
   */
  @ApiPropertyOptional({ description: 'Comisión mínima a filtrar' })
  @IsOptional()
  @IsNumber()
  @Min(0)
  comisionMinima?: number;

  /**
   * Filtro por comisión máxima. Solo muestra liquidaciones con comisión <= valor especificado.
   */
  @ApiPropertyOptional({ description: 'Comisión máxima a filtrar' })
  @IsOptional()
  @IsNumber()
  @Min(0)
  comisionMaxima?: number;

  /**
   * Estado de la liquidación de agencia a filtrar.
   * Ejemplos: 'pendiente', 'aprobada', 'pagada', 'anulada'
   */
  @ApiPropertyOptional({
    description: 'Estado de la liquidación de agencia',
    example: 'pendiente',
    enum: ['pendiente', 'aprobada', 'pagada', 'anulada'],
  })
  @IsOptional()
  @IsString()
  estado?: string;

  /**
   * MEJ-03 (Excel Facturación, PR5b): IDs exactos a traer, saltándose el
   * resto de los filtros y el rango de temporada por defecto (igual que la
   * búsqueda por documento, ver `buscaPorDocumento`). Tope de 2000 — un
   * excedente debe rechazarse (400), nunca truncarse en silencio.
   */
  @ApiPropertyOptional({
    description: 'IDs exactos de liquidación (Excel Facturación)',
    type: [Number],
  })
  @IsOptional()
  // PR6 (verify-report-pr5b/pr6): el DTO es el mismo del GET del listado, así
  // que `ids` debe validar también por query-string. Express entrega `?ids=1`
  // como string suelto y `?ids=1&ids=2` como string[]: se normaliza siempre a
  // number[] (un valor no numérico queda NaN y lo rechaza `@IsInt`).
  @Transform(({ value }) =>
    value === undefined || value === null
      ? value
      : (Array.isArray(value) ? value : [value]).map((v) =>
          typeof v === 'string' ? Number(v) : v
        )
  )
  @IsArray()
  @ArrayMaxSize(2000)
  @IsInt({ each: true })
  @IsPositive({ each: true })
  ids?: number[];

  /**
   * Página de resultados (paginación).
   */
  @ApiPropertyOptional({
    description: 'Número de página',
    type: Number,
    example: 1,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  page?: number;

  /**
   * Cantidad de resultados por página.
   */
  @ApiPropertyOptional({
    description: 'Elementos por página',
    type: Number,
    example: 10,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  limit?: number;

  /**
   * Columna por la que se ordena la consulta.
   */
  @ApiPropertyOptional({
    description:
      "Columna para ordenar (ej: 'fechaLiquidacion', 'comisionNeta', 'cliente')",
    example: 'fechaLiquidacion',
  })
  @IsOptional()
  @IsString()
  orderBy?: string;

  /**
   * Dirección de ordenamiento.
   */
  @ApiPropertyOptional({
    description: "Dirección de orden: 'ASC' o 'DESC'",
    enum: ['ASC', 'DESC'],
    example: 'DESC',
  })
  @IsOptional()
  @IsString()
  orderDirection?: 'ASC' | 'DESC';
}
